import type { NavKey } from "./types";

/** WHM-style tool definition for home icon grid + search */
export type WhmTool = {
  id: string;
  label: string;
  description: string;
  section: WhmSectionId;
  nav: NavKey;
  /** Optional hash/query for future deep links */
  hint?: string;
  /** Root-only (hidden from resellers) */
  rootOnly?: boolean;
  /** Highlight as “next step” for new installs */
  nextStep?: boolean;
};

export type WhmSectionId =
  | "home"
  | "accounts"
  | "plan"
  | "packages"
  | "domains"
  | "dns"
  | "email"
  | "sql"
  | "ssl"
  | "security"
  | "backup"
  | "transfers"
  | "import"
  | "server"
  | "services"
  | "apps"
  | "software"
  | "reseller"
  | "advanced";

export type WhmSection = {
  id: WhmSectionId;
  label: string;
  /** Primary nav key when section is clicked (first tool or hub) */
  nav?: NavKey;
};

/**
 * Left sidebar sections (WHM-like).
 * Plan (hosting plans) sits after Account.
 * Package (owner packages + resellers + VPS/reseller resource plans) is last.
 */
export const WHM_SECTIONS: WhmSection[] = [
  { id: "home", label: "Home", nav: "overview" },
  { id: "accounts", label: "Account Functions", nav: "accounts" },
  /** Hosting plans only — RAM/CPU/disk for customer accounts */
  { id: "plan", label: "Plan", nav: "plans" },
  { id: "domains", label: "Domains", nav: "sites" },
  { id: "dns", label: "DNS Functions", nav: "dns" },
  { id: "email", label: "Email", nav: "email" },
  { id: "sql", label: "SQL Services", nav: "databases" },
  { id: "ssl", label: "SSL/TLS", nav: "ssl" },
  { id: "security", label: "Security Center", nav: "security" },
  { id: "backup", label: "Backup", nav: "backups" },
  { id: "import", label: "Import", nav: "import" },
  { id: "transfers", label: "Transfers", nav: "migrations" },
  { id: "server", label: "Server Configuration", nav: "metrics" },
  { id: "apps", label: "Apps", nav: "apps" },
  { id: "software", label: "Software", nav: "polish" },
  { id: "advanced", label: "Advanced", nav: "advanced" },
  /** Owner packages, VPS/reseller resource plans, reseller center — bottom */
  { id: "packages", label: "Package", nav: "packages" }
];

/**
 * Reseller WHM — Plan (own hosting plans) after Account.
 * No Owner Packages / Package section (root-only).
 */
export const RESELLER_WHM_SECTIONS: WhmSection[] = [
  { id: "home", label: "Home", nav: "overview" },
  { id: "accounts", label: "Account Functions", nav: "accounts" },
  { id: "plan", label: "Plan", nav: "plans" },
  { id: "domains", label: "Domains", nav: "sites" },
  { id: "ssl", label: "SSL/TLS", nav: "ssl" },
  { id: "packages", label: "Branding", nav: "branding" },
  { id: "security", label: "Security", nav: "security" }
];

export const WHM_TOOLS: WhmTool[] = [
  // Account Functions
  {
    id: "list-accounts",
    label: "List Accounts",
    description: "View, suspend, and manage hosting accounts",
    section: "accounts",
    nav: "accounts",
    nextStep: true
  },
  {
    id: "create-account",
    label: "Create a New Account",
    description: "Username, package, password — home sets up automatically",
    section: "accounts",
    nav: "accounts",
    hint: "create",
    nextStep: true
  },
  {
    id: "modify-account",
    label: "Modify an Account",
    description: "Open an account from List Accounts to change settings",
    section: "accounts",
    nav: "accounts"
  },
  {
    id: "password",
    label: "Password / Security",
    description: "Change panel password and two-factor auth",
    section: "accounts",
    nav: "security"
  },
  // ── Plan section: hosting plans only (staff + reseller) ─────────────
  {
    id: "hosting-plans",
    label: "Hosting Plans",
    description: "Basic / Pro / Business — RAM, CPU, disk for customer accounts",
    section: "plan",
    nav: "plans",
    hint: "standard",
    nextStep: true
  },
  {
    id: "add-hosting-plan",
    label: "Add a Hosting Plan",
    description: "Create Basic / Pro / Business with RAM · CPU · storage",
    section: "plan",
    nav: "plans",
    hint: "standard"
  },
  {
    id: "edit-hosting-plans",
    label: "Edit Hosting Plans",
    description: "Update hosting plan resources and pricing details",
    section: "plan",
    nav: "plans",
    hint: "standard"
  },

  // ── Package section (bottom): owner licenses + VPS/reseller plans + resellers ─
  {
    id: "owner-packages",
    label: "Owner Packages (VPS / Reseller)",
    description:
      "Master products: price + account seats. Assign to server IPs under Licenses",
    section: "packages",
    nav: "packages",
    rootOnly: true,
    nextStep: true
  },
  {
    id: "vps-plans",
    label: "VPS Plans",
    description:
      "Resource plans for Direct User / small-business VPS accounts — superadmin managed",
    section: "packages",
    nav: "plans",
    hint: "vps",
    rootOnly: true
  },
  {
    id: "reseller-plans",
    label: "Reseller Plans",
    description:
      "Resource plans for resellers (seat pools) — view and manage all owners",
    section: "packages",
    nav: "plans",
    hint: "reseller",
    rootOnly: true
  },
  // DNS (W6)
  {
    id: "dns-zone",
    label: "DNS Zone Manager",
    description: "All zones · A/MX/TXT records · dig lookup",
    section: "dns",
    nav: "dns"
  },
  {
    id: "dns-lookup",
    label: "DNS Lookup",
    description: "dig / query tools for zone testing",
    section: "dns",
    nav: "dns"
  },
  // Email (W6)
  {
    id: "email",
    label: "Email Accounts",
    description: "Mailboxes, forwarders, MX routing",
    section: "email",
    nav: "email"
  },
  {
    id: "email-routing",
    label: "Mail Delivery",
    description: "Local vs external MX and DKIM",
    section: "email",
    nav: "email"
  },
  // SQL (W6)
  {
    id: "phpmyadmin",
    label: "phpMyAdmin",
    description: "Full phpMyAdmin SSO (all server databases)",
    section: "sql",
    nav: "databases"
  },
  {
    id: "mysql",
    label: "MySQL Databases",
    description: "Create databases, users, and grants",
    section: "sql",
    nav: "databases"
  },
  {
    id: "db-wizard",
    label: "Database Wizard",
    description: "Create DB → user → privileges in one flow",
    section: "sql",
    nav: "databases"
  },
  // Security (W7)
  {
    id: "security-advisor",
    label: "Security Advisor",
    description: "Hardening score, firewall, Fail2ban checklist",
    section: "security",
    nav: "security",
    rootOnly: true
  },
  {
    id: "firewall",
    label: "Firewall Configuration",
    description: "Ports and allow rules",
    section: "security",
    nav: "security",
    rootOnly: true
  },
  // SSL/TLS (dedicated top-level menu — Webuzo-style)
  {
    id: "auto-ssl",
    label: "Automatic SSL",
    description:
      "One-click Let's Encrypt · RSA 2048-bit · Install / Revoke / Renew",
    section: "ssl",
    nav: "ssl",
    nextStep: true
  },
  {
    id: "panel-domain-ssl",
    label: "Panel Domain SSL",
    description:
      "HTTPS for WHM / user panel hostnames (vps.example.com → ports 7001/7002)",
    section: "ssl",
    nav: "ssl",
    hint: "panel"
  },
  {
    id: "ssl-install-all",
    label: "Install All Certificates",
    description: "Issue SSL for every domain missing a certificate",
    section: "ssl",
    nav: "ssl",
    hint: "install-all"
  },
  {
    id: "ssl-logs",
    label: "SSL Logs",
    description: "Recent install / renew / revoke actions",
    section: "ssl",
    nav: "ssl",
    hint: "logs"
  },
  {
    id: "audit",
    label: "Audit Log",
    description: "Staff actions and account events",
    section: "security",
    nav: "audit",
    rootOnly: true
  },
  {
    id: "api-tokens",
    label: "API Tokens",
    description: "Machine access for billing and automation",
    section: "security",
    nav: "tokens",
    rootOnly: true
  },
  // Backup (W7)
  {
    id: "backup-config",
    label: "Backup Configuration",
    description: "Create, schedule, restore account backups",
    section: "backup",
    nav: "backups"
  },
  {
    id: "backup-restore",
    label: "Restore a Backup",
    description: "Restore home, DB, mail, or DNS from archive",
    section: "backup",
    nav: "backups"
  },
  // Import (Softaculous-style)
  {
    id: "import-webuzo",
    label: "Import From Webuzo",
    description: "Scan a remote Webuzo server and transfer accounts",
    section: "import",
    nav: "import",
    hint: "webuzo",
    rootOnly: true,
    nextStep: true
  },
  {
    id: "import-cpanel-remote",
    label: "Import from cPanel",
    description: "Scan a remote cPanel server and transfer accounts",
    section: "import",
    nav: "import",
    hint: "cpanel",
    rootOnly: true,
    nextStep: true
  },
  // Transfers (W7) — archive / cluster tools
  {
    id: "transfer",
    label: "Transfer Tool",
    description: "Export packages and move accounts between nodes",
    section: "transfers",
    nav: "migrations"
  },
  {
    id: "import-cpanel",
    label: "Import cPanel Backup",
    description: "Upload and inspect cPanel / RealPanel archives",
    section: "transfers",
    nav: "migrations"
  },
  // Server (W8)
  {
    id: "service-status",
    label: "Service Status",
    description: "API, agent, DB, DNS, mail green/red lights",
    section: "server",
    nav: "metrics",
    hint: "status",
    rootOnly: true
  },
  {
    id: "server-info",
    label: "Server Information",
    description: "OS, load, and system metrics",
    section: "server",
    nav: "metrics",
    hint: "metrics",
    rootOnly: true
  },
  {
    id: "panel-config",
    label: "Panel Configuration",
    description:
      "Hostname, IPs, NS, quota, PHP/FPM limits, panel ports (Webuzo-style)",
    section: "server",
    nav: "metrics",
    hint: "panel",
    rootOnly: true,
    nextStep: true
  },
  {
    id: "basic-config",
    label: "Basic Config",
    description: "Hostname, primary IP, contact email",
    section: "server",
    nav: "metrics",
    hint: "basic",
    rootOnly: true
  },
  {
    id: "tweaks",
    label: "Tweak Settings",
    description: "Panel feature flags",
    section: "server",
    nav: "metrics",
    hint: "tweaks",
    rootOnly: true
  },
  // Domains (Softaculous Domain List)
  {
    id: "domain-list",
    label: "Domain List",
    description: "All domains — users, paths, types, bulk delete, export CSV",
    section: "domains",
    nav: "sites",
    nextStep: true
  },
  {
    id: "add-domain",
    label: "Add Domain",
    description: "Create addon, subdomain, or alias for an account",
    section: "domains",
    nav: "sites",
    hint: "add"
  },
  {
    id: "all-sites",
    label: "List Websites",
    description: "Same as Domain List — all sites on this server",
    section: "domains",
    nav: "sites"
  },
  // Apps — full Webuzo-style Apps dropdown (order matches Webuzo)
  {
    id: "install-apps",
    label: "Install an App",
    description: "Stacks, PHP, MySQL, Nginx, Redis and more",
    section: "apps",
    nav: "apps",
    hint: "install",
    rootOnly: true,
    nextStep: true
  },
  {
    id: "installed-apps",
    label: "Installed Apps",
    description: "System packages installed or detected on this server",
    section: "apps",
    nav: "apps",
    hint: "installed",
    rootOnly: true
  },
  {
    id: "update-apps",
    label: "Update Apps",
    description: "Review installed system software for updates",
    section: "apps",
    nav: "apps",
    hint: "update",
    rootOnly: true
  },
  {
    id: "apps-services",
    label: "Services",
    description: "Service status (nginx, PHP-FPM, MySQL, mail…)",
    section: "apps",
    nav: "apps",
    hint: "services",
    rootOnly: true
  },
  {
    id: "enduser-apps",
    label: "Enduser Apps",
    description: "Website CMS apps for hosting accounts (WordPress…)",
    section: "apps",
    nav: "apps",
    hint: "website",
    rootOnly: true
  },
  {
    id: "directory-index",
    label: "DirectoryIndex",
    description: "Default index files for web servers",
    section: "apps",
    nav: "apps",
    hint: "directory-index",
    rootOnly: true
  },
  {
    id: "default-apps",
    label: "Default Apps",
    description: "Default software versions and install preferences",
    section: "apps",
    nav: "apps",
    hint: "default-apps",
    rootOnly: true
  },
  {
    id: "install-distro",
    label: "Install Distro Packages",
    description: "Install OS packages via apt/dnf (same as Install an App)",
    section: "apps",
    nav: "apps",
    hint: "install",
    rootOnly: true
  },
  {
    id: "apache-status",
    label: "Apache Status",
    description: "Apache / web server runtime status",
    section: "apps",
    nav: "apps",
    hint: "apache-status",
    rootOnly: true
  },
  {
    id: "apache-info",
    label: "Apache Server Information",
    description: "Web server build and configuration summary",
    section: "apps",
    nav: "apps",
    hint: "apache-info",
    rootOnly: true
  },
  {
    id: "apache-settings",
    label: "Apache Settings",
    description: "Web server kind, gateway, and basic HTTP settings",
    section: "apps",
    nav: "apps",
    hint: "apache-settings",
    rootOnly: true
  },
  {
    id: "apache-handlers",
    label: "Apache PHP Handlers",
    description: "How PHP is executed (PHP-FPM sockets)",
    section: "apps",
    nav: "apps",
    hint: "apache-handlers",
    rootOnly: true
  },
  {
    id: "apache-modules",
    label: "Apache Modules",
    description: "Loaded Apache/nginx modules and capabilities",
    section: "apps",
    nav: "apps",
    hint: "apache-modules",
    rootOnly: true
  },
  {
    id: "php-extensions",
    label: "PHP Extensions",
    description: "Enable or disable PHP extensions",
    section: "apps",
    nav: "apps",
    hint: "php-extensions",
    rootOnly: true
  },
  {
    id: "multiphp-manager",
    label: "MultiPHP Manager",
    description: "Default PHP version and MultiPHP tools",
    section: "apps",
    nav: "apps",
    hint: "multiphp",
    rootOnly: true
  },
  {
    id: "php-fpm-settings",
    label: "PHP FPM Settings",
    description: "PHP-FPM pool limits (max children, requests…)",
    section: "apps",
    nav: "apps",
    hint: "php-fpm",
    rootOnly: true
  },
  {
    id: "php-pear",
    label: "PHP PEAR packages",
    description: "PEAR/PECL package management",
    section: "apps",
    nav: "apps",
    hint: "php-pear",
    rootOnly: true
  },
  {
    id: "edit-php-ini",
    label: "Edit PHP INI",
    description: "Panel PHP defaults (memory, upload, execution time)",
    section: "apps",
    nav: "apps",
    hint: "php-ini",
    rootOnly: true
  },
  {
    id: "mysql-config",
    label: "MySQL Configuration",
    description: "Database engine status and configuration",
    section: "apps",
    nav: "apps",
    hint: "mysql-config",
    rootOnly: true
  },
  {
    id: "perl-module",
    label: "Install a Perl Module",
    description: "Install Perl modules via package manager / cpan",
    section: "apps",
    nav: "apps",
    hint: "perl-module",
    rootOnly: true
  },
  {
    id: "ftp-config",
    label: "FTP Server Config",
    description: "FTP public host, ports, and virtual users",
    section: "apps",
    nav: "apps",
    hint: "ftp-config",
    rootOnly: true
  },
  // Software / appearance
  {
    id: "appearance",
    label: "Appearance",
    description: "Theme, language, and panel plugins",
    section: "software",
    nav: "polish",
    hint: "appearance"
  },
  // Advanced (W9) — cluster off by default
  {
    id: "licenses",
    label: "Licenses (Server IPs)",
    description:
      "Assign Owner VPS/Reseller packages to server IPs · generate license keys",
    section: "advanced",
    nav: "licenses",
    hint: "licenses",
    rootOnly: true,
    nextStep: true
  },
  {
    id: "advanced-overview",
    label: "Advanced Settings",
    description: "Optional power-user flags (cluster, limits)",
    section: "advanced",
    nav: "advanced",
    hint: "overview",
    rootOnly: true
  },
  {
    id: "cluster",
    label: "Server Nodes",
    description: "Multi-server cluster (enable under Advanced first)",
    section: "advanced",
    nav: "servers",
    hint: "cluster",
    rootOnly: true
  },
  {
    id: "resource-limits",
    label: "Resource Limits",
    description: "CloudLinux LVE / CageFS per-account limits",
    section: "advanced",
    nav: "isolation",
    hint: "limits",
    rootOnly: true
  },
  {
    id: "integrations",
    label: "Integrations",
    description: "Webhooks, WHMCS, and billing hooks",
    section: "advanced",
    nav: "webhooks",
    hint: "integrations",
    rootOnly: true
  },
  // Resellers merged into Package (no separate Resellers menu)
  {
    id: "reseller-center",
    label: "Reseller Center",
    description: "Create resellers and resource pools",
    section: "packages",
    nav: "resellers",
    rootOnly: true
  },
  {
    id: "branding",
    label: "Branding",
    description: "Logo, product name, nameservers",
    section: "packages",
    nav: "branding"
  }
];

const FAV_KEY = "realpanel_whm_favorites_v1";
const FAV_KEY_RESELLER = "realpanel_reseller_fav_v1";

export function loadFavorites(role?: string): string[] {
  const key = role === "reseller" ? FAV_KEY_RESELLER : FAV_KEY;
  const defaults =
    role === "reseller"
      ? ["list-accounts", "create-account", "hosting-plans", "branding"]
      : ["list-accounts", "create-account", "phpmyadmin", "hosting-plans"];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed : defaults;
  } catch {
    return defaults;
  }
}

export function saveFavorites(ids: string[], role?: string) {
  const key = role === "reseller" ? FAV_KEY_RESELLER : FAV_KEY;
  localStorage.setItem(key, JSON.stringify(ids));
}

export function toggleFavorite(id: string, role?: string): string[] {
  const cur = loadFavorites(role);
  const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
  saveFavorites(next, role);
  return next;
}

export function toolsForRole(role: string): WhmTool[] {
  if (role === "superadmin") return WHM_TOOLS;
  // Normal Admin: full WHM tools except Super-Admin-only Owner Packages
  if (role === "admin") {
    return WHM_TOOLS.filter((t) => {
      if (t.id === "owner-packages") return false;
      if (t.id === "vps-plans" || t.id === "reseller-plans") return false;
      // Package section commercial tools are Super Admin only
      if (t.section === "packages" && t.id !== "branding") return false;
      return true;
    });
  }
  // Reseller WHM: accounts, plan (hosting plans), branding, domains, ssl, security
  return WHM_TOOLS.filter((t) => {
    if (t.rootOnly) return false;
    if (t.id === "reseller-center") return false;
    if (t.id === "owner-packages") return false;
    if (t.id === "vps-plans" || t.id === "reseller-plans") return false;
    if (t.id === "password") return true;
    if (t.id === "branding") return true;
    return (
      t.section === "accounts" ||
      t.section === "plan" ||
      t.section === "domains" ||
      t.section === "ssl" ||
      t.section === "security"
    );
  });
}

export function sectionsForRole(role: string): WhmSection[] {
  if (role === "reseller") return RESELLER_WHM_SECTIONS;
  // Package (Owner Packages) menu is Super Admin only
  if (role === "admin") {
    return WHM_SECTIONS.filter((s) => s.id !== "packages");
  }
  return WHM_SECTIONS;
}
